import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import useQueryString from '@sorare/core/src/hooks/useQueryString';
import { navLabels } from '@sorare/core/src/lib/glossary';

import { USInfoModal } from '@sorare/us-sports/src/components/USInfoModal';
import { Cards } from '@sorare/us-sports/src/components/gallery/Cards';

import { ClubLayout } from '@sorare/manager/src/components/profile/ClubLayout';
import { myClubFilters } from 'components/searchCards/cardFilters';

import {
  BaseballMyClubPageQuery,
  BaseballMyClubPageQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const Wrapper = styled(Vertical).attrs({ gap: 2 })``;

const BASEBALL_MY_CLUB_PAGE_QUERY = gql`
  query BaseballMyClubPageQuery(
    $slug: String!
    $name: String
    $hasDeck: Boolean!
  ) {
    user(slug: $slug) {
      slug
      id
      baseballUserProfile {
        id
        deck(name: $name) @include(if: $hasDeck) {
          slug
          name
          socialPictureUrls {
            post
          }
        }
        decks {
          nodes {
            slug
          }
        }
        gallerySocialPictureUrls {
          post
        }
      }
      ...Cards_user
    }
  }
  ${Cards.fragments.user}
` as TypedDocumentNode<
  BaseballMyClubPageQuery,
  BaseballMyClubPageQueryVariables
>;

const UserCards = () => {
  const {
    flags: { useMlbSeasonAverageScore = true },
  } = useFeatureFlags();
  const { slug } = useTypedParams<PageParams>();
  const { currentUser } = useCurrentUserContext();
  const currentDeckName = useQueryString(SEARCH_PARAMS.DECK);

  const initialDeckName = useRef(currentDeckName);
  const { data } = useQuery(BASEBALL_MY_CLUB_PAGE_QUERY, {
    variables: {
      slug,
      name: initialDeckName.current || '',
      hasDeck: !!initialDeckName.current,
    },
  });
  const { user } = data || {};

  const isCurrentUser = user && user.slug === currentUser?.slug;
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();

  const deck =
    (currentDeckName === initialDeckName.current &&
      user?.baseballUserProfile?.deck) ??
    undefined;

  useEffect(() => {
    if (user) {
      if (deck) {
        return setPageMetadata(deck.name, {
          img: deck?.socialPictureUrls.post,
        });
      }
      return setPageMetadata(formatMessage(navLabels.gallery), {
        img: user.baseballUserProfile?.gallerySocialPictureUrls.post,
      });
    }
    return () => {};
  }, [deck, formatMessage, setPageMetadata, user]);

  const nbDecks = user?.baseballUserProfile?.decks?.nodes.length || 0;

  const showDecksFilter = isCurrentUser || nbDecks > 0;
  const galleryFilters = useMemo(
    () => myClubFilters(useMlbSeasonAverageScore, showDecksFilter),
    [useMlbSeasonAverageScore, showDecksFilter]
  );

  if (!user) {
    return <LoadingIndicator fullHeight />;
  }

  return (
    <ClubLayout cards>
      <PageHeader text={navLabels.gallery} />
      <Wrapper>
        <Cards
          sport={Sport.BASEBALL}
          user={user}
          galleryFilters={galleryFilters}
          PlayerDetailsComponent={USInfoModal}
        />
      </Wrapper>
    </ClubLayout>
  );
};

export default UserCards;
